import React, { useCallback, useContext, useState } from 'react'

import { createPortal } from 'react-dom'

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    ListItem,
    TextField,
    Tooltip
} from '@mui/material'

import LoadingButton from '@mui/lab/LoadingButton'

import AddCircleIcon from '@mui/icons-material/AddCircle'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HelpIcon from '@mui/icons-material/Help'
import SaveIcon from '@mui/icons-material/Save'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import { FixedSizeList } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'

import ResponsiveImportButton from '../../../Clients/ResponsiveImportButton'
import Loading from '../../../Loading'
import useNotification from '../../../Notification'
import { ScreenContext } from '../..'
import { QueueSocket } from '../../../../routes'
import api from '../../../../api'
import { getSession } from '../../../../auth.js'
import { clearImportedText, normalizeImportHeader, readImportedFileAsText, replicateFirstRowSegments } from '../import-utils'
import { isValidCnpj, normalizeCnpj } from '../../../../utils/masks'

import '../ImportClients/styles.css'

const onlyDigits = value => (value || '').replace(/[^0-9]+/g, '')

const enterprise = getSession()?.profile.enterprise
const importLimit = enterprise?.settings?.crm_upload_limit || 5000

const formatCnpj = (value) => normalizeCnpj(value).slice(0, 14)

const maxLengthPos = {
    name: 256,
    social_reason: 256,
    cnpj: 14,
    telephone: 15,
    email: 128,
    code: 64,
    field: 1024,
    domain: 100,
    linkedin_url: 256,
    address: 1024,
    address_number: 10,
    address_extra: 256,
    neighborhood: 128,
    city: 128,
    estate: 128,
    postal_code: 9,
    comments: 99999,
    segmentos: 99999
}

const labels = {
    name: 'Nome',
    social_reason: 'Razão Social',
    cnpj: 'CNPJ',
    telephone: 'Telefone',
    email: 'E-mail',
    code: 'Código',
    field: 'Área',
    domain: 'Domínio',
    linkedin_url: 'LinkedIn',
    address: 'Endereço',
    address_number: 'Número',
    address_extra: 'Complemento',
    neighborhood: 'Bairro',
    city: 'Cidade',
    estate: 'Estado',
    postal_code: 'CEP',
    comments: 'Comentários',
    segmentos: 'Segmentos'
}

const validate = (item, pos) => {
    const errors = []
    const value = item[pos] || ''

    if (pos === 'name' && !value.trim())
        errors.push('Nome é obrigatório.')

    if (maxLengthPos[pos] && value.length > maxLengthPos[pos])
        errors.push(`${labels[pos] || pos} não pode ter mais de ${maxLengthPos[pos]} caracteres.`)

    if (pos === 'cnpj' && value && !isValidCnpj(value))
        errors.push('CNPJ inválido.')

    if (errors.length === 0)
        return ''

    return errors.map(error => (<Box>{error}</Box>))
}

const revalidate = (data) => {
    if (data.length > importLimit) {
        useNotification(
            'Ops!',
            `Limite de ${importLimit} empresas excedido.`,
            'danger'
        )
        data = data.slice(0, importLimit)
    }

    data.map(row => {
        const errors = Object.keys(labels).some(pos => validate(row, pos))
        row.error = errors
    })

    return data.sort((x, y) => Boolean(y.error) - Boolean(x.error))
}

const reposition = (data) => {
    data.some((each, index) => { each.position = index })
    return data
}

const RowEdit = ({ item, pos, label, revalidate }) => {
    const [editing, setEditing] = useState(false)
    const [loadeditem, setLoadedItem] = useState(item[pos])
    const [error, setError] = useState([])

    const submit = () => {
        let normalized = loadeditem
        if (pos === 'cnpj')
            normalized = normalizeCnpj(loadeditem).slice(0, 14)
        if (pos === 'telephone' || pos === 'postal_code')
            normalized = onlyDigits(loadeditem)

        setEditing(false)
        item[pos] = normalized
        setLoadedItem(normalized)
        revalidate()
    }

    React.useEffect(() => {
        setError(validate(item, pos))
    }, [editing])

    return (
        <Tooltip title={error} describeChild={true}>
            <span className={error ? 'error' : ''}>
                {!editing ?
                    <>
                        <Box>{loadeditem}</Box>
                        <Tooltip title={label}>
                            <Button onClick={() => setEditing(true)} className='action-button'>
                                <EditIcon />
                            </Button>
                        </Tooltip>
                    </>
                    :
                    <>
                        <TextField
                            className='edit-import-input'
                            value={loadeditem}
                            variant='standard'
                            onChange={(e) => {
                                if (pos === 'cnpj') {
                                    setLoadedItem(normalizeCnpj(e.target.value).slice(0, 14))
                                    return
                                }
                                if (pos === 'telephone' || pos === 'postal_code') {
                                    setLoadedItem(onlyDigits(e.target.value))
                                    return
                                }
                                setLoadedItem(e.target.value)
                            }}
                            inputProps={{ maxLength: maxLengthPos[pos] }}
                        />
                        <Tooltip title={'Salvar'}>
                            <Button onClick={submit} className='action-button'><SaveIcon /></Button>
                        </Tooltip>
                    </>
                }
            </span>
        </Tooltip>
    )
}

const possibleFields = {
    nome: 'name',
    name: 'name',
    empresa: 'name',
    'razao social': 'social_reason',
    'razão social': 'social_reason',
    'social reason': 'social_reason',
    social_reason: 'social_reason',
    cnpj: 'cnpj',
    telefone: 'telephone',
    telephone: 'telephone',
    email: 'email',
    codigo: 'code',
    code: 'code',
    area: 'field',
    'área': 'field',
    field: 'field',
    dominio: 'domain',
    'domínio': 'domain',
    domain: 'domain',
    linkedin: 'linkedin_url',
    'linkedin url': 'linkedin_url',
    linkedin_url: 'linkedin_url',
    endereco: 'address',
    'endereço': 'address',
    address: 'address',
    numero: 'address_number',
    'número': 'address_number',
    'address number': 'address_number',
    address_number: 'address_number',
    complemento: 'address_extra',
    'address extra': 'address_extra',
    address_extra: 'address_extra',
    bairro: 'neighborhood',
    neighborhood: 'neighborhood',
    cidade: 'city',
    city: 'city',
    estado: 'estate',
    estate: 'estate',
    uf: 'estate',
    cep: 'postal_code',
    'postal code': 'postal_code',
    postal_code: 'postal_code',
    comentarios: 'comments',
    'comentários': 'comments',
    comments: 'comments',
    segmentos: 'segmentos',
    groups: 'segmentos'
}

const ImportEnterprises = () => {
    const { enterpriseId, permissions, settings, customFields } = useContext(ScreenContext)
    const { setCache } = useContext(QueueSocket)
    const [loadedData, updateLoadedData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [extraColumns, setExtraColumns] = useState([])

    const errorCallback = useCallback((message) => {
        useNotification(
            'Ops!',
            message,
            'danger'
        )
    }, [])

    const handleReplicateSegments = () => {
        const newData = replicateFirstRowSegments(loadedData)
        if (newData) {
            updateLoadedData(newData)
        }
    }

    const RowItem = (props) => {
        const { index, style } = props
        const item = loadedData.find(item => item.position === index)

        if (!item)
            return (<></>)

        return (
            <ListItem style={style} className={`import-contact-display-item item ${item.error ? 'error' : ''}`} key={`import-enterprise-item${item.id}`}>
                {Object.keys(labels).map(pos => (
                    <RowEdit key={pos} item={item} pos={pos} label={`Editar ${labels[pos]}`} revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
                ))}
                {extraColumns.map(col => (
                    <RowEdit key={col.key} item={item} pos={col.key} label={`Editar ${col.label}`} revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
                ))}
                <span className='item-last'>
                    {item.db_error && (
                        <Tooltip title={item.db_error}>
                            <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: '1.2rem', cursor: 'help', alignSelf: 'center' }} />
                        </Tooltip>
                    )}
                    <Tooltip title='Excluir linha'>
                        <Button onClick={() => updateLoadedData(reposition(loadedData.filter(each => each !== item)))} className='action-button delete'>
                            <DeleteIcon />
                        </Button>
                    </Tooltip>
                </span>
            </ListItem>
        )
    }

    const onChangeImportEnterprises = async () => {
        const oldCache = localStorage.getItem('uploadCache')

        if (oldCache) {
            useNotification(
                'Ops!',
                'Já existe uma importação em andamento..',
                'danger'
            )
            return
        }

        const newCache = Math.random()
        const formData = new FormData()
        formData.append('cache_key', newCache)

        const normalizedLoadedData = (loadedData || []).map(row => ({
            ...row,
            cnpj: formatCnpj(row.cnpj || ''),
            telephone: onlyDigits(row.telephone || ''),
            postal_code: onlyDigits(row.postal_code || '')
        }))

        const json = JSON.stringify(normalizedLoadedData)
        const blob = new Blob([json], { type: 'application/json' })
        formData.append('data', blob)

        updateLoadedData(null)
        setExtraColumns([])

        setLoading(true)
        const response = await api.post(`/api/enterprises/${enterpriseId}/contact_enterprises/upload_enterprises/`, formData, { timeout: 0 })
        setLoading(false)

        if (response.status === 200) {
            useNotification(
                'Por favor aguarde!',
                'A importação em massa pode demorar e ficará disponível na aba abaixo. Continue a usar o kwik normalmente.',
                'info'
            )
            setCache(newCache)
            return
        }

        useNotification(
            'Ops!',
            'Ocorreu um erro ao importar empresas.',
            'danger'
        )
    }

    const detectSeparator = (header, separatorIndex, trySeparators, fieldIndexes, extraFields) => {
        let realSeparator = trySeparators[separatorIndex]
        if (!realSeparator)
            return false

        if (realSeparator === ',')
            realSeparator = /,(?=(?:[^"]*"[^"]*")*[^"]*$)/

        let count = 0
        Object.keys(extraFields).forEach(key => delete extraFields[key])

        header.split(realSeparator).map((field, index) => {
            const selectedField = normalizeImportHeader(field, true)
            if (selectedField in possibleFields) {
                fieldIndexes[possibleFields[selectedField]] = index + 1
                count++
            } else if (selectedField && selectedField.trim()) {
                extraFields[selectedField] = {
                    index: index + 1,
                    label: clearImportedText(field).replace(/['"]/g, '').trim()
                }
            }
        })

        if (count === 0)
            return detectSeparator(header, separatorIndex + 1, trySeparators, fieldIndexes, extraFields)
        return realSeparator
    }

    const parseArray = (array) => {
        const rawdata = array.split(/\r?\n/)
        if (rawdata.length === 0) {
            setLoading(false)
            return errorCallback('Arquivo inválido')
        }

        const fieldIndexes = Object.fromEntries(Object.keys(labels).map(key => [key, 0]))
        const extraFields = {}
        const realSeparator = detectSeparator(rawdata[0], 0, [',', ';', '|'], fieldIndexes, extraFields)

        if (!realSeparator) {
            setLoading(false)
            return errorCallback('Não há cabeçalhos, é necessário conter pelo menos nome e o separador deve ser , ou ; ou |')
        }

        rawdata.shift()

        const modData = rawdata.map((row, index) => {
            if (row === '')
                return

            const separatedData = clearImportedText(row).split(realSeparator)
            const data = ['', ...separatedData]
            const returndata = { id: index }

            Object.keys(labels).forEach(field => {
                const idx = fieldIndexes[field]
                returndata[field] = idx && data[idx] ? data[idx].trim().replaceAll('"', '') : ''
            })

            returndata.cnpj = formatCnpj(returndata.cnpj)
            returndata.telephone = onlyDigits(returndata.telephone)
            returndata.postal_code = onlyDigits(returndata.postal_code)

            Object.entries(extraFields).forEach(([fieldName, fieldInfo]) => {
                const fieldIdx = fieldInfo.index
                if (fieldIdx && fieldIdx < data.length)
                    returndata[fieldName] = data[fieldIdx] ? data[fieldIdx].trim() : ''
            })

            return returndata
        })

        const newdata = revalidate(modData.filter(d => d))
        updateLoadedData(reposition(newdata))
        setLoading(false)

        const customFieldNames = (customFields || []).map(cf => normalizeImportHeader(cf.field_name, true))
        const detectedExtra = Object.entries(extraFields)
            .filter(([, fieldInfo]) => fieldInfo.index > 0)
            .map(([fieldName, fieldInfo]) => {
                const isCustomField = customFieldNames.includes(fieldName)
                return {
                    key: fieldName,
                    label: fieldInfo.label || fieldName,
                    isCustomField
                }
            })
        setExtraColumns(detectedExtra)
    }

    const prepareData = (file) => {
        const oldCache = localStorage.getItem('uploadCache')

        if (oldCache) {
            useNotification(
                'Ops!',
                'Já existe uma importação em andamento..',
                'danger'
            )
            return
        }

        const reader = new FileReader()
        const eventFile = file
        reader.onload = function (e) {
            let text

            try {
                text = readImportedFileAsText(eventFile, e.target.result)
            } catch (err) {
                setLoading(false)
                return errorCallback('Arquivo inválido')
            }

            parseArray(text)
        }
        reader.readAsArrayBuffer(eventFile)
        setLoading(true)
    }

    const addNovaEntrada = () => {
        if (loadedData.length >= importLimit)
            return

        const newitem = Object.fromEntries(Object.keys(labels).map(key => [key, '']))
        extraColumns.forEach(col => { newitem[col.key] = '' })
        updateLoadedData(reposition(revalidate([newitem, ...loadedData])))
    }

    const errors = loadedData?.filter(each => each.error).length
    const validationErrors = loadedData?.filter(each => each.error && !each.db_error).length

    return (
        <>
            {createPortal((<Loading loading={loading} />), document.body)}
            {loadedData && <>
                <Dialog
                    open={true}
                    fullWidth
                    maxWidth='xl'
                    disableEnforceFocus
                    className='import-contact-dialog'
                >
                    <DialogTitle id="customized-dialog-title" className={'group-flexbox'}>
                        <span>Importar empresas</span>
                        <Button sx={{ minWidth: '2rem', padding: '0' }} onClick={() => { updateLoadedData(null); setExtraColumns([]) }}>
                            <CloseIcon />
                        </Button>
                    </DialogTitle>
                    <Divider />
                    <DialogContent className='import-contact-content'>
                        <Box className='import-contact-display-item'>
                            <Box>Quantidade de empresas: {loadedData.length} / {importLimit}</Box>
                            <Box>Quantidade de empresas com erros: {errors}</Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <LoadingButton
                                size="small"
                                component="label"
                                sx={{ alignSelf: 'self-start', height: '100%' }}
                                startIcon={<AddCircleIcon />}
                                onClick={addNovaEntrada}
                                disabled={loadedData.length >= importLimit}
                            >
                                Adicionar
                            </LoadingButton>
                            <LoadingButton
                                size="small"
                                component="label"
                                sx={{ alignSelf: 'flex-end' }}
                                onClick={handleReplicateSegments}
                            >
                                Replicar segmentos
                            </LoadingButton>
                        </Box>
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <ListItem className='import-contact-display-item header' key='import-enterprise-header'>
                                {Object.values(labels).map(label => (
                                    <span key={label}>{label}</span>
                                ))}
                                {extraColumns.map(col => (
                                    <Tooltip
                                        key={col.key}
                                        title={col.isCustomField
                                            ? `Campo customizado: ${col.label}`
                                            : `Coluna "${col.label}" não corresponde a nenhum campo do sistema. Será ignorada.`
                                        }
                                        placement="bottom"
                                        disableInteractive
                                    >
                                        <span>
                                            <div className='import-contact-segment-icon'>
                                                {col.isCustomField
                                                    ? <>{col.label} <HelpIcon className="question-icon" /></>
                                                    : <><span style={{ color: '#d32f2f', textDecoration: 'line-through' }}>{col.label}</span> <WarningAmberIcon className="question-icon" sx={{ color: '#d32f2f' }} /></>
                                                }
                                            </div>
                                        </span>
                                    </Tooltip>
                                ))}
                                <span className='item-last'></span>
                            </ListItem>
                            <Box sx={{ height: '100%' }}>
                                <AutoSizer>
                                    {({ height, width }) => (
                                        <FixedSizeList
                                            height={height}
                                            width={width}
                                            itemSize={40}
                                            itemCount={loadedData.length}
                                            overscanCount={10}
                                        >
                                            {RowItem}
                                        </FixedSizeList>
                                    )}
                                </AutoSizer>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions style={{ padding: '10px 20px 10px 10px' }}>
                        <LoadingButton
                            onClick={onChangeImportEnterprises}
                            color="primary"
                            disabled={(validationErrors > 0 || loadedData.length === 0)}
                        >
                            Enviar
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </>}

            <ListItem className={'menuItem'} sx={{ padding: 0 }}>
                <ResponsiveImportButton
                    className="containedImportButton"
                    onClickFn={() => {}}
                    isDisabled={false}
                    title="Importar"
                    shouldRenderHiddenInput={permissions && permissions.isSupervisor}
                    onChangeFn={prepareData}
                    is_financial_market_company={settings.is_financial_market_company}
                    dialogTitle="Importação em massa de empresas"
                    informationType="enterprises"
                />
            </ListItem>
        </>
    )
}

export default ImportEnterprises
